import { css } from '@emotion/react';

import { classes as baseClasses } from '@core/ButtonBase/getButtonBaseCss';
import generateEmotionClasses from '@core/utils/generateEmotionClasses';
import isGhostVariant from '@core/utils/isGhostVariant';

import type { BaseProps } from './IconButtonBase';

export const classes = generateEmotionClasses('iconButton', [
  'round',
  'disabled',
  'small',
  'medium',
  'primary',
  'primaryInvert',
  'secondary',
  'secondaryNeutral',
  'ghost',
  'ghostInvert',
  'ghostSecondary',
]);

const shouldRemoveOuterSpace = (props: Partial<BaseProps>): boolean => {
  return isGhostVariant(props.variant) && props.edgeAlign === 'noPadding';
};

/**
 * Gets the size dimensions for icon buttons based on variant and edgeAlign.
 * Ghost variants with noPadding use smaller dimensions (icon-only size).
 */
export const getSizeDimensions = (props: Partial<BaseProps>) => {
  const removeOuterSpace = shouldRemoveOuterSpace(props);

  return {
    small: removeOuterSpace ? '20px' : '36px',
    medium: removeOuterSpace ? '24px' : '48px',
  };
};

export default (props: Partial<BaseProps>) => css`
  box-shadow: inset 0 0 0 1px var(--iconButton-boxShadowColor);
  background-color: var(--iconButton-background);
  color: var(--iconButton-color);

  &.${classes.small} {
    height: ${getSizeDimensions(props).small};
    width: ${getSizeDimensions(props).small};
  }

  &.${classes.medium} {
    height: ${getSizeDimensions(props).medium};
    width: ${getSizeDimensions(props).medium};
  }

  &.${classes.primary} {
    --iconButton-boxShadowColor: var(--cds-color-interactive-primary);
    --iconButton-background: var(--cds-color-interactive-primary);
    --iconButton-color: var(--cds-color-neutral-primary-invert);

    @media (hover: hover) {
      &:hover {
        --iconButton-background: var(--cds-color-interactive-primary-hover);
        --iconButton-boxShadowColor: var(--cds-color-interactive-primary-hover);
      }
    }

    &.${baseClasses.focusVisible} {
      --iconButton-background: var(--cds-color-interactive-primary-hover);
      --iconButton-boxShadowColor: var(--cds-color-interactive-primary-hover);
    }

    &:active {
      --iconButton-background: var(--cds-color-interactive-primary-pressed);
      --iconButton-boxShadowColor: var(--cds-color-interactive-primary-pressed);
    }

    &.${classes.disabled} {
      --iconButton-background: var(--cds-color-neutral-disabled-strong);
      --iconButton-boxShadowColor: var(--cds-color-neutral-disabled-strong);
      --iconButton-color: var(--cds-color-neutral-disabled);
    }
  }

  &.${classes.primaryInvert} {
    --iconButton-boxShadowColor: var(--cds-color-neutral-primary-invert);
    --iconButton-background: var(--cds-color-neutral-background-primary);
    --iconButton-color: var(--cds-color-interactive-primary);

    @media (hover: hover) {
      &:hover {
        --iconButton-background: var(
          --cds-color-interactive-background-primary-hover-weak
        );
        --iconButton-boxShadowColor: var(--cds-color-neutral-primary-invert);
        --iconButton-color: var(--cds-color-interactive-primary-hover);
      }
    }

    &.${baseClasses.focusVisible} {
      --iconButton-background: var(
        --cds-color-interactive-background-primary-hover-weak
      );
      --iconButton-boxShadowColor: var(--cds-color-neutral-primary-invert);
      --iconButton-color: var(--cds-color-interactive-primary-hover);
    }

    &:active {
      --iconButton-boxShadowColor: var(--cds-color-neutral-primary-invert);
      --iconButton-background: var(
        --cds-color-interactive-background-primary-pressed-weak
      );
      --iconButton-color: var(--cds-color-interactive-primary-pressed);
    }

    &.${classes.disabled} {
      --iconButton-boxShadowColor: var(
        --cds-color-neutral-background-primary-weak
      );
      --iconButton-background: var(--cds-color-neutral-background-primary-weak);
      --iconButton-color: var(--cds-color-neutral-disabled-strong);
    }
  }

  &.${classes.secondary} {
    --iconButton-boxShadowColor: var(--cds-color-interactive-primary);
    --iconButton-background: var(--cds-color-neutral-background-primary);
    --iconButton-color: var(--cds-color-interactive-primary);

    @media (hover: hover) {
      &:hover {
        --iconButton-background: var(
          --cds-color-interactive-background-primary-hover-weak
        );
        --iconButton-boxShadowColor: var(--cds-color-interactive-primary-hover);
        --iconButton-color: var(--cds-color-interactive-primary-hover);
      }
    }

    &.${baseClasses.focusVisible} {
      --iconButton-background: var(
        --cds-color-interactive-background-primary-hover-weak
      );
      --iconButton-boxShadowColor: var(--cds-color-interactive-primary-hover);
      --iconButton-color: var(--cds-color-interactive-primary-hover);
    }

    &:active {
      --iconButton-boxShadowColor: var(--cds-color-interactive-primary-pressed);
      --iconButton-background: var(
        --cds-color-interactive-background-primary-pressed-weak
      );
      --iconButton-color: var(--cds-color-interactive-primary-pressed);
    }

    &.${classes.disabled} {
      --iconButton-boxShadowColor: var(--cds-color-neutral-disabled-strong);
      --iconButton-background: var(--cds-color-neutral-background-primary-weak);
      --iconButton-color: var(--cds-color-neutral-disabled-strong);
    }
  }

  &.${classes.secondaryNeutral} {
    --iconButton-boxShadowColor: var(--cds-color-neutral-stroke-primary-weak);
    --iconButton-background: var(--cds-color-neutral-background-primary);
    --iconButton-color: var(--cds-color-interactive-secondary);

    @media (hover: hover) {
      &:hover {
        --iconButton-background: var(
          --cds-color-interactive-background-primary-hover-weak
        );
        --iconButton-boxShadowColor: var(--cds-color-neutral-stroke-primary);
        --iconButton-color: var(--cds-color-interactive-secondary-hover);
      }
    }

    &.${baseClasses.focusVisible} {
      --iconButton-background: var(
        --cds-color-interactive-background-primary-hover-weak
      );
      --iconButton-boxShadowColor: var(--cds-color-neutral-stroke-primary);
      --iconButton-color: var(--cds-color-interactive-secondary-hover);
    }

    &:active {
      --iconButton-boxShadowColor: var(--cds-color-neutral-stroke-primary);
      --iconButton-background: var(
        --cds-color-interactive-background-secondary-selected-weak
      );
      --iconButton-color: var(--cds-color-interactive-secondary-pressed);
    }

    &.${classes.disabled} {
      --iconButton-boxShadowColor: var(--cds-color-neutral-stroke-primary-weak);
      --iconButton-background: var(--cds-color-neutral-disabled-weak);
      --iconButton-color: var(--cds-color-neutral-disabled-strong);
    }
  }

  &.${classes.ghost} {
    --iconButton-boxShadowColor: transparent;
    --iconButton-background: unset;
    --iconButton-color: var(--cds-color-interactive-primary);

    @media (hover: hover) {
      &:hover {
        --iconButton-background: var(
          --cds-color-interactive-background-primary-hover-weak
        );
        --iconButton-color: var(--cds-color-interactive-primary-hover);
      }
    }

    &.${baseClasses.focusVisible} {
      --iconButton-background: var(
        --cds-color-interactive-background-primary-hover-weak
      );
      --iconButton-color: var(--cds-color-interactive-primary-hover);
    }

    &:active {
      --iconButton-background: var(
        --cds-color-interactive-background-primary-pressed-weak
      );
      --iconButton-color: var(--cds-color-interactive-primary-pressed);
    }

    &.${classes.disabled} {
      --iconButton-color: var(--cds-color-neutral-disabled-strong);
    }
  }

  &.${classes.ghostInvert} {
    --iconButton-boxShadowColor: transparent;
    --iconButton-background: unset;
    --iconButton-color: var(--cds-color-neutral-primary-invert);

    @media (hover: hover) {
      &:hover {
        --iconButton-boxShadowColor: var(--cds-color-neutral-primary-invert);
      }
    }

    &.${baseClasses.focusVisible}, &:active {
      --iconButton-boxShadowColor: var(--cds-color-neutral-primary-invert);
    }

    &.${classes.disabled} {
      --iconButton-color: var(--cds-color-neutral-disabled-strong);
    }
  }

  &.${classes.ghostSecondary} {
    --iconButton-boxShadowColor: transparent;
    --iconButton-background: unset;
    --iconButton-color: var(--cds-color-interactive-secondary);

    @media (hover: hover) {
      &:hover {
        --iconButton-background: var(
          --cds-color-interactive-background-secondary-hover-weak
        );
        --iconButton-color: var(--cds-color-interactive-secondary-hover);
      }
    }

    &.${baseClasses.focusVisible} {
      --iconButton-background: var(
        --cds-color-interactive-background-secondary-hover-weak
      );
      --iconButton-color: var(--cds-color-interactive-secondary-hover);
    }

    &:active {
      --iconButton-background: var(
        --cds-color-interactive-background-secondary-pressed-weak
      );
      --iconButton-color: var(--cds-color-interactive-secondary-pressed);
    }

    &.${classes.disabled} {
      --iconButton-color: var(--cds-color-neutral-disabled-strong);
    }
  }

  &.${classes.round} {
    border-radius: var(--cds-border-radius-max);

    &.${baseClasses.focusVisible}::after {
      border-radius: var(--cds-border-radius-max);
    }
  }
`;
